/**
 * AddConditionModal
 *
 * Modal for creating a new lender condition for a credit application.
 * Fields: conditionType, conditionCategory, conditionDescription,
 *         shortName, priority, dueDate, responsibleParty, brokerNotes.
 *
 * Creates via POST /api/applications/:applicationId/conditions
 * F007 AC002
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ── Options ──────────────────────────────────────────────────────────────────

const CONDITION_TYPE_OPTIONS = [
  { value: "precedent", label: "Precedent" },
  { value: "subsequent", label: "Subsequent" },
  { value: "ongoing", label: "Ongoing" },
];

const CONDITION_CATEGORY_OPTIONS = [
  { value: "documentation", label: "Documentation" },
  { value: "valuation", label: "Valuation" },
  { value: "insurance", label: "Insurance" },
  { value: "legal", label: "Legal" },
  { value: "financial", label: "Financial" },
  { value: "security", label: "Security" },
  { value: "other", label: "Other" },
];

const PRIORITY_OPTIONS = [
  { value: "1", label: "High" },
  { value: "2", label: "Medium" },
  { value: "3", label: "Low" },
];

// ── Component ────────────────────────────────────────────────────────────────

interface AddConditionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
}

export function AddConditionModal({
  open,
  onOpenChange,
  applicationId,
}: AddConditionModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [conditionType, setConditionType] = useState("precedent");
  const [conditionCategory, setConditionCategory] = useState("documentation");
  const [conditionDescription, setConditionDescription] = useState("");
  const [shortName, setShortName] = useState("");
  const [priority, setPriority] = useState("2");
  const [dueDate, setDueDate] = useState("");
  const [responsibleParty, setResponsibleParty] = useState("");
  const [brokerNotes, setBrokerNotes] = useState("");

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setConditionType("precedent");
      setConditionCategory("documentation");
      setConditionDescription("");
      setShortName("");
      setPriority("2");
      setDueDate("");
      setResponsibleParty("");
      setBrokerNotes("");
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", `/api/applications/${applicationId}/conditions`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/applications/${applicationId}/conditions`],
      });
      toast({ title: "Condition added" });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create condition",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!conditionDescription.trim()) {
      toast({
        title: "Validation Error",
        description: "Condition description is required",
        variant: "destructive",
      });
      return;
    }

    createMutation.mutate({
      conditionType,
      conditionCategory,
      conditionDescription: conditionDescription.trim(),
      conditionShortName: shortName.trim() || null,
      priority: parseInt(priority, 10),
      dueDate: dueDate || null,
      responsibleParty: responsibleParty.trim() || null,
      brokerNotes: brokerNotes.trim() || null,
      conditionStatus: "outstanding",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[550px]"
        data-testid="dialog-add-condition"
      >
        <DialogHeader>
          <DialogTitle>Add Condition</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          {/* Condition Type */}
          <div className="space-y-1.5">
            <Label>Condition Type</Label>
            <Select value={conditionType} onValueChange={setConditionType}>
              <SelectTrigger data-testid="select-condition-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {CONDITION_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={conditionCategory} onValueChange={setConditionCategory}>
              <SelectTrigger data-testid="select-condition-category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {CONDITION_CATEGORY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label>Description *</Label>
            <Textarea
              placeholder="Describe the condition requirement..."
              value={conditionDescription}
              onChange={(e) => setConditionDescription(e.target.value)}
              rows={3}
              data-testid="input-condition-description"
            />
          </div>

          {/* Short Name */}
          <div className="space-y-1.5">
            <Label>Short Name</Label>
            <Input
              placeholder="e.g. Proof of Income"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              data-testid="input-condition-short-name"
            />
          </div>

          {/* Priority */}
          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="select-condition-priority">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Due Date */}
          <div className="space-y-1.5">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              data-testid="input-condition-due-date"
            />
          </div>

          {/* Responsible Party */}
          <div className="space-y-1.5">
            <Label>Responsible Party</Label>
            <Input
              placeholder="e.g. Borrower, Solicitor"
              value={responsibleParty}
              onChange={(e) => setResponsibleParty(e.target.value)}
              data-testid="input-responsible-party"
            />
          </div>

          {/* Broker Notes */}
          <div className="space-y-1.5">
            <Label>Broker Notes</Label>
            <Textarea
              placeholder="Internal notes..."
              value={brokerNotes}
              onChange={(e) => setBrokerNotes(e.target.value)}
              rows={2}
              data-testid="input-broker-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
            data-testid="button-cancel-condition"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            data-testid="button-submit-condition"
          >
            {createMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Add Condition
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
